import { SB } from '@play-co/replicant';
import { AlternativeOpenWebBuckets } from 'src/replicant/utils/payload';
import {
  jankenMoves,
  jankenOutcomes,
  JankenOutcome,
} from 'src/replicant/janken/janken.ruleset';
import {
  createFlexContainer,
  createFlexMessageTemplate,
  createOpenGameAction,
} from './utils';
import { FlexComponent } from './types';
import {
  getPetAssetKey,
  speciesAssetsRatio,
  speciesChatbot,
  SpeciesType,
} from './species';

export const jankenAssets = {
  // Challenge card assets (client-side)
  'janken/bg': 'private/chatbot/janken/janken_bg.png',
  'janken/invite': 'private/chatbot/janken/janken_invite.png',
  'janken/green_button': 'private/chatbot/janken/janken_green_button.png',
  'janken/blue_outcome': 'private/chatbot/janken/janken_blue_outcome.png',
  'janken/red_outcome': 'private/chatbot/janken/janken_red_outcome.png',
  // Result card — outcome label images (per outcome × locale)
  'janken/result_win_en': 'private/chatbot/janken/janken_win_en.png',
  'janken/result_win_ja': 'private/chatbot/janken/janken_win_ja.png',
  'janken/result_lose_en': 'private/chatbot/janken/janken_lose_en.png',
  'janken/result_lose_ja': 'private/chatbot/janken/janken_lose_ja.png',
  'janken/result_draw_en': 'private/chatbot/janken/janken_draw_en.png',
  'janken/result_draw_ja': 'private/chatbot/janken/janken_draw_ja.png',
  // Result card — sign icons
  'janken/sign_rock': 'private/chatbot/janken/janken_sign_rock.png',
  'janken/sign_paper': 'private/chatbot/janken/janken_sign_paper.png',
  'janken/sign_scissors': 'private/chatbot/janken/janken_sign_scissors.png',
};

// -----------------------------------------------------------------------------
// Challenge flex (client-side, for share target picker)
// -----------------------------------------------------------------------------

/**
 * Create a janken challenge flex message for the LINE share target picker.
 *
 * Composed from image fragments with absolute-positioned overlays:
 * background arena + signs capsule + sender pet + localized text.
 * Client-side: caller resolves asset URLs and text before calling.
 */
export function createJankenChallengeFlexMessage(opts: {
  text: string;
  altText: string;
  cta: string;
  bgUrl: string;
  signsUrl: string;
  petUrl: string;
  petAspectRatio: string;
  buttonUrl: string;
  payload: object & { alternativeWebIABBucket: AlternativeOpenWebBuckets };
}) {
  const {
    text,
    altText,
    cta,
    bgUrl,
    signsUrl,
    petUrl,
    petAspectRatio,
    buttonUrl,
    payload,
  } = opts;

  return createFlexContainer(altText, {
    type: 'bubble',
    size: 'giga',
    action: createOpenGameAction(altText, payload),
    body: {
      type: 'box',
      layout: 'vertical',
      paddingAll: '0px',
      contents: [
        // Background: arena/ring scene
        {
          type: 'image',
          url: bgUrl,
          size: 'full',
          aspectRatio: '1.51:1',
          aspectMode: 'cover',
          gravity: 'center',
        },
        // Text overlay: challenge text on LED board area
        {
          type: 'box',
          layout: 'vertical',
          contents: [
            {
              type: 'text',
              text,
              color: '#FFFFFF',
              weight: 'bold',
              size: 'xxl',
              align: 'center',
              wrap: true,
              maxLines: 2,
              adjustMode: 'shrink-to-fit',
            },
          ],
          position: 'absolute',
          width: '100%',
          height: '15%',
          offsetTop: '0%',
          offsetStart: '0%',
          paddingStart: '5%',
          paddingEnd: '5%',
          justifyContent: 'center',
          alignItems: 'center',
        },
        // Pet species overlay: sender's pet on the ring
        {
          type: 'box',
          layout: 'vertical',
          contents: [
            {
              type: 'image',
              url: petUrl,
              size: 'full',
              aspectRatio: petAspectRatio,
            },
          ],
          position: 'absolute',
          width: '33%',
          height: '60%',
          offsetBottom: '22%',
          offsetStart: '6%',
          justifyContent: 'flex-end',
        },
        // Signs capsule overlay: rock/paper/scissors
        {
          type: 'box',
          layout: 'vertical',
          contents: [
            {
              type: 'image',
              url: signsUrl,
              size: 'full',
              aspectRatio: '3:1',
              aspectMode: 'fit',
            },
          ],
          position: 'absolute',
          width: '80%',
          height: '59%',
          offsetTop: '20%',
          offsetEnd: '-10%',
          justifyContent: 'center',
        },
        // Play button overlay (last = on top of all other elements)
        {
          type: 'box',
          layout: 'vertical',
          contents: [
            {
              type: 'image',
              url: buttonUrl,
              size: 'full',
              aspectRatio: '5:1',
              aspectMode: 'fit',
            },
            {
              type: 'box',
              layout: 'vertical',
              contents: [
                {
                  type: 'text',
                  text: cta,
                  color: '#FFFFFF',
                  weight: 'bold',
                  size: '3xl',
                  align: 'center',
                  adjustMode: 'shrink-to-fit',
                },
              ],
              position: 'absolute',
              width: '100%',
              height: '80%',
              offsetTop: '4%',
              justifyContent: 'center',
              alignItems: 'center',
            },
          ],
          position: 'absolute',
          width: '70%',
          offsetBottom: '3%',
          offsetStart: '15%',
          justifyContent: 'center',
          alignItems: 'center',
          action: createOpenGameAction(cta, payload),
        },
      ] as FlexComponent[],
    },
  });
}

// -----------------------------------------------------------------------------
// Result OA notification (server-side)
// -----------------------------------------------------------------------------

const outcomeConfig: Record<
  JankenOutcome,
  Record<'en' | 'ja', { title: (name: string) => string; cta: string }>
> = {
  win: {
    ja: {
      title: (name) => `${name}との\nじゃんけんに勝った！`,
      cta: '結果を見る',
    },
    en: {
      title: (name) => `You won a Janken match\nagainst ${name}`,
      cta: 'View Result',
    },
  },
  lose: {
    ja: {
      title: (name) => `${name}との\nじゃんけんに負けた…`,
      cta: 'リベンジ？',
    },
    en: {
      title: (name) => `You lost a Janken match\nagainst ${name}`,
      cta: 'Rematch?',
    },
  },
  draw: {
    ja: {
      title: (name) => `${name}との\nじゃんけんは引き分け！`,
      cta: 'もう一回？',
    },
    en: {
      title: (name) => `You tied a Janken match\nagainst ${name}`,
      cta: 'One more?',
    },
  },
};

/** Per-outcome pet emotion: sender's emotion maps to the outcome perspective. */
const outcomeEmotions: Record<
  JankenOutcome,
  { sender: SpeciesType; opponent: SpeciesType }
> = {
  win: { sender: 'happy', opponent: 'verysad' },
  lose: { sender: 'verysad', opponent: 'happy' },
  draw: { sender: 'normal', opponent: 'normal' },
};

/** Aspect ratios for result label images (measured from PNGs). */
const resultLabelRatios: Record<JankenOutcome, Record<'en' | 'ja', string>> = {
  win: { en: '345:180', ja: '462:218' },
  lose: { en: '675:185', ja: '461:218' },
  draw: { en: '450:185', ja: '510:218' },
};

/**
 * Janken result OA notification flex message template.
 * Sent to the challenge sender after the receiver resolves the challenge.
 *
 * Composed from image fragments with absolute-positioned overlays:
 * background arena + pets (sender/opponent) + sign circles + result label + CTA button.
 * Positioning reuses values from createJankenChallengeFlexMessage where elements overlap.
 */
export const jankenResultMessage = createFlexMessageTemplate(
  SB.object({
    locale: SB.tuple(['en', 'ja']),
    outcome: SB.tuple(jankenOutcomes),
    opponentName: SB.string(),
    senderMove: SB.tuple(jankenMoves),
    receiverMove: SB.tuple(jankenMoves),
    senderSpecies: SB.tuple(speciesChatbot),
    opponentSpecies: SB.tuple(speciesChatbot),
  }),
  (data, payload, api) => {
    const locale = data.locale as 'en' | 'ja';
    const outcome = data.outcome as JankenOutcome;
    const config = outcomeConfig[outcome][locale];
    const title = config.title(data.opponentName);

    // Pet overlays — emotion depends on outcome
    const emotions = outcomeEmotions[outcome];
    const senderPetKey = getPetAssetKey(data.senderSpecies, emotions.sender);
    const opponentPetKey = getPetAssetKey(
      data.opponentSpecies,
      emotions.opponent,
    );

    return createFlexContainer(title, {
      type: 'bubble',
      size: 'giga',
      action: createOpenGameAction(title, payload),
      body: {
        type: 'box',
        layout: 'vertical',
        paddingAll: '0px',
        contents: [
          // 1. Background: arena/ring scene
          {
            type: 'image',
            url: api.getAssetPath('janken/bg'),
            size: 'full',
            aspectRatio: '1.51:1',
            aspectMode: 'cover',
            gravity: 'center',
          },
          // 2. LED board text overlay (reuses challenge card positioning)
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'text',
                text: title,
                color: '#FFFFFF',
                weight: 'bold',
                size: 'xxl',
                align: 'center',
                wrap: true,
                maxLines: 2,
                adjustMode: 'shrink-to-fit',
              },
            ],
            position: 'absolute',
            width: '100%',
            height: '15%',
            offsetTop: '0%',
            offsetStart: '0%',
            paddingStart: '5%',
            paddingEnd: '5%',
            justifyContent: 'center',
            alignItems: 'center',
          },
          // 3. Left pet: sender (reuses challenge card positioning)
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: api.getAssetPath(senderPetKey),
                size: 'full',
                aspectRatio: speciesAssetsRatio[senderPetKey] ?? '1:1',
              },
            ],
            position: 'absolute',
            width: '29%',
            height: '55%',
            offsetBottom: '22%',
            offsetStart: '2%',
            justifyContent: 'flex-end',
          },
          // 4. Right pet: opponent (mirrored from challenge card)
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: api.getAssetPath(opponentPetKey),
                size: 'full',
                aspectRatio: speciesAssetsRatio[opponentPetKey] ?? '1:1',
              },
            ],
            position: 'absolute',
            width: '29%',
            height: '55%',
            offsetBottom: '22%',
            offsetEnd: '2%',
            justifyContent: 'flex-end',
          },
          // 5. Left sign circle: sender's move (red)
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: api.getAssetPath('janken/red_outcome'),
                size: 'full',
                aspectRatio: '1:1',
                aspectMode: 'cover',
              },
              {
                type: 'box',
                layout: 'vertical',
                contents: [
                  {
                    type: 'image',
                    url: api.getAssetPath(
                      `janken/sign_${data.senderMove}` as keyof typeof jankenAssets,
                    ),
                    size: 'full',
                    aspectRatio: '1:1',
                    aspectMode: 'fit',
                  },
                ],
                position: 'absolute',
                width: '55%',
                height: '55%',
                offsetTop: '22.5%',
                offsetStart: '22.5%',
                justifyContent: 'center',
                alignItems: 'center',
              },
            ],
            position: 'absolute',
            width: '26%',
            offsetTop: '21%',
            offsetStart: '24%',
          },
          // 6. Right sign circle: opponent's move (blue)
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: api.getAssetPath('janken/blue_outcome'),
                size: 'full',
                aspectRatio: '1:1',
                aspectMode: 'cover',
              },
              {
                type: 'box',
                layout: 'vertical',
                contents: [
                  {
                    type: 'image',
                    url: api.getAssetPath(
                      `janken/sign_${data.receiverMove}` as keyof typeof jankenAssets,
                    ),
                    size: 'full',
                    aspectRatio: '1:1',
                    aspectMode: 'fit',
                  },
                ],
                position: 'absolute',
                width: '55%',
                height: '55%',
                offsetTop: '22.5%',
                offsetStart: '22.5%',
                justifyContent: 'center',
                alignItems: 'center',
              },
            ],
            position: 'absolute',
            width: '26%',
            offsetTop: '21%',
            offsetEnd: '24%',
          },
          // 7. Result label ("Win!" / "Lose!" / "Draw!")
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: api.getAssetPath(
                  `janken/result_${outcome}_${locale}` as keyof typeof jankenAssets,
                ),
                size: 'full',
                aspectRatio: resultLabelRatios[outcome][locale],
                aspectMode: 'fit',
              },
            ],
            position: 'absolute',
            width: '25%',
            offsetTop: '56%',
            offsetStart: '37.5%',
            justifyContent: 'center',
            alignItems: 'center',
          },
          // 8. CTA button + text overlay (reuses challenge card positioning)
          {
            type: 'box',
            layout: 'vertical',
            contents: [
              {
                type: 'image',
                url: api.getAssetPath('janken/green_button'),
                size: 'full',
                aspectRatio: '5:1',
                aspectMode: 'fit',
              },
              {
                type: 'box',
                layout: 'vertical',
                contents: [
                  {
                    type: 'text',
                    text: config.cta,
                    color: '#FFFFFF',
                    weight: 'bold',
                    size: '3xl',
                    align: 'center',
                    adjustMode: 'shrink-to-fit',
                  },
                ],
                position: 'absolute',
                width: '100%',
                height: '80%',
                offsetTop: '4%',
                justifyContent: 'center',
                alignItems: 'center',
              },
            ],
            position: 'absolute',
            width: '70%',
            offsetBottom: '3%',
            offsetStart: '15%',
            justifyContent: 'center',
            alignItems: 'center',
            action: createOpenGameAction(config.cta, payload),
          },
        ] as FlexComponent[],
      },
    });
  },
);
